import { PDFDocument, PDFPage, PDFImage, StandardFonts } from 'pdf-lib';
// TODO: Optimize custom font loading - currently impacts performance
// import fontkit from '@pdf-lib/fontkit';
import JSZip from 'jszip';
import { logger } from 'src/logger';
import { getBaseballCardByLogin } from 'src/api/baseball-card-service';
import { PDF_FIELD_MAPPINGS, BATCH_SIZE, BATCH_DELAY_MS } from '../constants/BaseballCardConstants';
import { OrgChartEmployee } from '../types/orgChart.types';
import { getImageFromS3URI } from 'src/services/aws/S3Service';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { getLocalDateForJSZip } from 'src/utilities/DateTimeUtilities';

/**
 * Helper function to check if an employee has a valid job level.
 * Returns false if jobLevel is null, undefined, or empty string.
 */
function hasValidJobLevel(employee: OrgChartEmployee): boolean {
  return employee.jobLevel !== null && employee.jobLevel !== undefined && employee.jobLevel.trim() !== '';
}

function getCurrentQuarter(): { year: number; quarter: number } {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  return {
    year: now.getFullYear(),
    quarter: Math.ceil(currentMonth / 3)
  };
}

function calculateCurrentQuarterForDisplay(): string {
  const { year, quarter } = getCurrentQuarter();
  return `${year} Q${quarter}`;
}

function calculateCurrentQuarterForFilename(): string {
  const { year, quarter } = getCurrentQuarter();
  return `${year}_Q${quarter}`;
}

function sanitizeFilename(name: string): string {
  const sanitized = name.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_');
  // Fallback to 'unknown' if sanitization results in empty string
  return sanitized || 'unknown';
}

export interface PdfGenerationResult {
  pdfBlob: Blob;
  employeeFullName: string;
  hadApiError?: boolean;
}

function convertDaysToYearMonth(days: number): { years: number; months: number } {
  let years = Math.floor(days / 365);
  const remainingDays = days % 365;
  let months = Math.floor(remainingDays / 30);

  // Normalize months: if months >= 12, roll over to additional year(s)
  if (months >= 12) {
    years += Math.floor(months / 12);
    months = months % 12;
  }

  return { years, months };
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ensures all parent folders in a path exist with correct local timestamps.
 * JSZip creates folders implicitly but they get default/UTC timestamps.
 * This function explicitly creates folders so they have the correct local timestamp.
 */
function ensureFoldersWithLocalTimestamp(zip: JSZip, filePath: string): void {
  const parts = filePath.split('/');
  let currentPath = '';

  // Skip the last part (filename) and create all parent folders
  for (let i = 0; i < parts.length - 1; i++) {
    currentPath += parts[i] + '/';
    // Only create if folder doesn't already exist
    if (!zip.files[currentPath]) {
      zip.file(currentPath, null as unknown as string, { dir: true, date: getLocalDateForJSZip() });
    }
  }
}

// TODO: Optimize custom font loading - currently impacts performance significantly
// Module-level cache for font bytes to avoid repeated network requests during bulk PDF generation
// let cachedFontBytes: ArrayBuffer | null = null;
//
// async function getFontBytes(): Promise<ArrayBuffer> {
//   if (!cachedFontBytes) {
//     const fontUrl = '/fonts/Amazon-Ember-Medium.ttf';
//     const fontResponse = await fetch(fontUrl);
//     if (!fontResponse.ok) {
//       throw new Error(`Failed to fetch Amazon Ember font: ${fontResponse.statusText}`);
//     }
//     cachedFontBytes = await fontResponse.arrayBuffer();
//   }
//   return cachedFontBytes;
// }

// Module-level cache for PDF template bytes to avoid repeated network requests during bulk PDF generation
let cachedPdfTemplateBytes: ArrayBuffer | null = null;
let cachedPdfTemplatePath: string | null = null;

async function getPdfTemplateBytes(pdfTemplatePath: string): Promise<ArrayBuffer> {
  // If template path changed, invalidate cache
  if (cachedPdfTemplatePath !== pdfTemplatePath) {
    cachedPdfTemplateBytes = null;
    cachedPdfTemplatePath = pdfTemplatePath;
  }

  if (!cachedPdfTemplateBytes) {
    const response = await fetch(pdfTemplatePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF template: ${response.statusText}`);
    }
    cachedPdfTemplateBytes = await response.arrayBuffer();
  }
  return cachedPdfTemplateBytes;
}

// S3 bucket name for baseball cards avatars (e.g., baseball-cards-alpha, baseball-cards-prod)
const stage = ENVIRONMENT_VARIABLES.env.Stage === 'dev' ? 'alpha' : ENVIRONMENT_VARIABLES.env.Stage;
const BASEBALL_CARDS_BUCKET = `baseball-cards-${stage}`;

/**
 * Builds an S3 URI for an avatar image
 * @param alias - The employee alias
 * @param size - The image size ('large' or 'medium')
 * @returns The S3 URI in format: s3://baseball-cards-prod/avatars/{alias}/{size}.png
 */
function buildAvatarS3URI(alias: string, size: 'large' | 'medium'): string {
  return `s3://${BASEBALL_CARDS_BUCKET}/avatars/${alias}/${size}.png`;
}

async function fetchImageFromS3(s3URI: string): Promise<ArrayBuffer | null> {
  try {
    const imageData = await getImageFromS3URI(s3URI);
    if (!imageData) {
      logger.warn('Image fetch returned null', { s3URI });
      return null;
    }
    return imageData;
  } catch (error) {
    logger.warn('Image fetch failed with exception', { s3URI, error });
    return null;
  }
}

export async function generatePdfForEmployee(pdfTemplatePath: string, employeeAlias: string): Promise<PdfGenerationResult> {
  const enrichedData: any = {};
  let baseballCard: any = null;

  // Generate static links - code links will only be included for technical employees
  const currentYear = new Date().getFullYear() - 1;
  const atozLinks = [
    `• https://atoz.amazon.work/manager/profiles/${employeeAlias}/performance`,
    `• https://atoz.amazon.work/manager/profiles/${employeeAlias}/employee-details`
  ];
  const codeLinks = [
    `• https://code.amazon.com/api/asci/changes_for_user?from_date=${currentYear}-01-01&to_date=${currentYear}-12-31&user=${employeeAlias}`,
    `• https://code.amazon.com/users/${employeeAlias}/activity-stats-cr.json?start_time=${currentYear}-01-01&end_time=${currentYear}-12-31`
  ];

  // Helper function to build sources links based on technical classification
  const buildSourcesLinks = (isTechnical: boolean): string => {
    const links = isTechnical ? [...atozLinks, ...codeLinks] : atozLinks;
    return ['Sources:', ...links].join('\n');
  };

  // Set default values - default to all links (will be adjusted after classification is determined)
  enrichedData.formattedJustification = '';
  enrichedData.formattedPrimaryOwnership = buildSourcesLinks(true);
  enrichedData.talentCardPeriod = calculateCurrentQuarterForDisplay();

  // Track if API call failed (returns null) - PDF will still be generated with default data
  let hadApiError = false;

  try {
    baseballCard = await getBaseballCardByLogin(employeeAlias);

    if (baseballCard?.baseballCard) {
      const cardData = baseballCard.baseballCard;
      enrichedData._cardData = cardData;

      if (cardData.timeInRole !== undefined && cardData.timeInRole !== null) {
        const roleTime = convertDaysToYearMonth(cardData.timeInRole);
        enrichedData.timeInRoleYears = String(roleTime.years);
        enrichedData.timeInRoleMonths = String(roleTime.months);
      }

      if (cardData.timeInLevel !== undefined && cardData.timeInLevel !== null) {
        const levelTime = convertDaysToYearMonth(cardData.timeInLevel);
        enrichedData.timeInLevelYears = String(levelTime.years);
        enrichedData.timeInLevelMonths = String(levelTime.months);
      }

      if (cardData.timeInAmazon !== undefined && cardData.timeInAmazon !== null) {
        const amazonTime = convertDaysToYearMonth(cardData.timeInAmazon);
        enrichedData.timeInAmazonYears = String(amazonTime.years);
        enrichedData.timeInAmazonMonths = String(amazonTime.months);
      }

      enrichedData.formattedJobHistory = cardData.jobHistorySummary?.summary || 'No job history summary available';
      enrichedData.formattedRoleSummary = cardData.roleSummary?.summary || '';
      enrichedData.isManager = cardData.isManager;
      enrichedData.isBarRaiser = cardData.isBarRaiser;

      let isTechnical = false;
      if (cardData.currentJobProfile) {
        const jobProfile = cardData.currentJobProfile;

        if (jobProfile.jobFamily?.id) {
          enrichedData.jobFamily = jobProfile.jobFamily.id;
        }

        if (jobProfile.name) {
          enrichedData.jobProfileName = jobProfile.name;
        }

        if (jobProfile.jobClassifications && Array.isArray(jobProfile.jobClassifications)) {
          const technicalClassification = jobProfile.jobClassifications.find(
            (classification: any) => classification.group === 'Technical Classification'
          );

          if (technicalClassification?.jobCodeClassification) {
            const fullClassification = technicalClassification.jobCodeClassification;
            let shortenedClassification = fullClassification;
            if (fullClassification.toLowerCase() === 'technical') {
              shortenedClassification = 'Tech';
              isTechnical = true;
            } else if (fullClassification.toLowerCase() === 'non-technical' || fullClassification.toLowerCase() === 'nontechnical') {
              shortenedClassification = 'Non-Tech';
            }
            enrichedData.technicalClassification = shortenedClassification;
          } else {
            enrichedData.technicalClassification = 'Non-Tech';
          }
        } else {
          // Few of the VP does not have the group technical classifications so defaulting to tech here
          isTechnical = true;
          enrichedData.technicalClassification = 'Tech';
          logger.debug('No jobClassifications array found', { employeeAlias, jobClassifications: jobProfile.jobClassifications });
        }
      } else {
        // Few of the VP does not have the group technical classifications so defaulting to tech here
        isTechnical = true;
        enrichedData.technicalClassification = 'Tech';
        logger.debug('No currentJobProfile found', { employeeAlias });
      }

      // Set justification summary (no links)
      enrichedData.formattedJustification = cardData.justification?.summary || '';

      // Build sources links based on technical classification
      const sourcesLinks = buildSourcesLinks(isTechnical);
      const primaryOwnershipSummary = cardData.primaryOwnership?.summary || '';
      enrichedData.formattedPrimaryOwnership = primaryOwnershipSummary ? `${primaryOwnershipSummary}\n\n${sourcesLinks}` : sourcesLinks;
    } else {
      // API returned null - treat as API error but continue with default data
      hadApiError = true;
      logger.warn('Baseball card API returned null, generating PDF with default data', { employeeAlias });
    }
  } catch (error) {
    // API threw an exception - treat as API error but continue with default data
    hadApiError = true;
    logger.warn('Failed to fetch baseball card data, generating PDF with default data', { employeeAlias, error });
  }

  // Use cached PDF template bytes to avoid repeated network requests during bulk generation
  const pdfBytes = await getPdfTemplateBytes(pdfTemplatePath);
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();

  // TODO: Re-enable custom Amazon Ember font once performance is optimized
  // Currently using standard Helvetica for better performance
  // pdfDoc.registerFontkit(fontkit);
  // const fontBytes = await getFontBytes();
  // const amazonEmberFont = await pdfDoc.embedFont(fontBytes, { subset: true });
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

  Object.entries(PDF_FIELD_MAPPINGS).forEach(([fieldName, dataKey]) => {
    if (!dataKey) return;
    if (fieldName.startsWith('checkbox-')) return;

    const value = enrichedData[dataKey];
    if (value !== undefined && value !== null) {
      try {
        const field = form.getTextField(fieldName);
        field.setText(String(value));
      } catch (error) {
        logger.debug('Text field not found in PDF template', { fieldName, dataKey, employeeAlias, error });
      }
    }
  });

  Object.entries(PDF_FIELD_MAPPINGS).forEach(([fieldName, dataKey]) => {
    if (!dataKey || !fieldName.startsWith('checkbox-')) return;

    const value = enrichedData[dataKey];
    if (value !== undefined && value !== null) {
      try {
        const checkbox = form.getCheckBox(fieldName);
        if (value === true) {
          checkbox.check();
        } else {
          checkbox.uncheck();
        }
      } catch (error) {
        logger.debug('Checkbox field not found in PDF template', { fieldName, dataKey, employeeAlias, error });
      }
    }
  });

  // Update all field appearances with Helvetica font
  // TODO: Switch back to Amazon Ember font once custom font performance is optimized
  form.updateFieldAppearances(helveticaFont);

  try {
    const cardData = enrichedData._cardData;

    if (cardData) {
      const fetchImageWithLogging = async (alias: string | undefined, size: 'large' | 'medium', imageType: string): Promise<ArrayBuffer | null> => {
        if (!alias) {
          logger.debug('No alias provided for image fetch', { imageType, employeeAlias });
          return null;
        }
        const s3URI = buildAvatarS3URI(alias, size);
        try {
          const result = await fetchImageFromS3(s3URI);
          if (!result) {
            logger.warn('Failed to fetch image', { imageType, employeeAlias, alias, s3URI });
          }
          return result;
        } catch (error) {
          logger.warn('Exception while fetching image', { imageType, employeeAlias, alias, s3URI, error });
          return null;
        }
      };

      // Get aliases for employee, manager, and skip manager
      const managerAlias = cardData.directManagerInfo?.login;
      const skipManagerAlias = cardData.skipManagerInfo?.login;

      const capturePromises = [
        fetchImageWithLogging(employeeAlias, 'large', 'employee'),
        fetchImageWithLogging(managerAlias, 'medium', 'manager'),
        fetchImageWithLogging(skipManagerAlias, 'medium', 'skipManager')
      ];

      const [employeeImageData, managerImageData, skipManagerImageData] = await Promise.all(capturePromises);

      const drawImageOnPage = async (page: PDFPage, image: PDFImage, buttonFieldName: string, imageType: string): Promise<boolean> => {
        try {
          const photoButton = form.getButton(buttonFieldName);
          const widgets = photoButton.acroField.getWidgets();

          if (widgets.length === 0) {
            logger.warn('No widgets found for image button', { buttonFieldName, imageType, employeeAlias });
            return false;
          }

          const rect = widgets[0].getRectangle();
          const { x, y, width, height } = rect;

          page.drawImage(image, { x, y, width, height });
          form.removeField(photoButton);

          return true;
        } catch (error) {
          logger.warn('Failed to draw image on page', { buttonFieldName, imageType, employeeAlias, error });
          return false;
        }
      };

      const embedImageWithFallback = async (imageData: ArrayBuffer, imageType: string): Promise<PDFImage | null> => {
        try {
          return await pdfDoc.embedPng(imageData);
        } catch (pngError) {
          try {
            return await pdfDoc.embedJpg(imageData);
          } catch (jpgError) {
            logger.warn('Failed to embed image as PNG or JPG', { imageType, employeeAlias, pngError, jpgError });
            return null;
          }
        }
      };

      const page = pdfDoc.getPage(0);

      if (employeeImageData) {
        const image = await embedImageWithFallback(employeeImageData, 'employee');
        if (image) {
          await drawImageOnPage(page, image, 'image-employee_af_image', 'employee');
        }
      }

      if (managerImageData) {
        const image = await embedImageWithFallback(managerImageData, 'manager');
        if (image) {
          await drawImageOnPage(page, image, 'image-manager_af_image', 'manager');
        }
      }

      if (skipManagerImageData) {
        const image = await embedImageWithFallback(skipManagerImageData, 'skipManager');
        if (image) {
          await drawImageOnPage(page, image, 'image-skip_af_image', 'skipManager');
        }
      }
    }
  } catch (error) {
    logger.warn('Avatar capture process failed', { employeeAlias, error });
  }

  const pdfBytesResult = await pdfDoc.save();
  const pdfBlob = new Blob([new Uint8Array(pdfBytesResult)], { type: 'application/pdf' });

  let employeeFullName = employeeAlias;
  if (baseballCard?.baseballCard?.employeeInfo) {
    const { firstName, lastName } = baseballCard.baseballCard.employeeInfo;
    if (firstName && lastName) {
      employeeFullName = `${firstName} ${lastName}`;
    }
  }

  return { pdfBlob, employeeFullName, hadApiError };
}

function buildNestedFolderPath(employee: OrgChartEmployee, employeeMap: Map<string, OrgChartEmployee>, leaderLogin: string): string {
  const pathParts: string[] = [];
  let currentLogin = employee.managerLogin;

  while (currentLogin && currentLogin !== leaderLogin) {
    pathParts.unshift(currentLogin);
    const manager = employeeMap.get(currentLogin);
    currentLogin = manager?.managerLogin;
  }

  return pathParts.length > 0 ? pathParts.join('/') + '/' : '';
}

export function buildFolderStructure(employees: OrgChartEmployee[], leaderLogin: string): Map<string, string[]> {
  const folderStructure = new Map<string, string[]>();

  for (const employee of employees) {
    if (employee.login === leaderLogin) {
      continue;
    }

    if (employee.managerLogin) {
      const managerEmployees = folderStructure.get(employee.managerLogin) || [];
      managerEmployees.push(employee.login);
      folderStructure.set(employee.managerLogin, managerEmployees);
    } else {
      const rootEmployees = folderStructure.get('') || [];
      rootEmployees.push(employee.login);
      folderStructure.set('', rootEmployees);
    }
  }

  return folderStructure;
}

export async function generateBulkPdfsAsZipWithFolders(
  pdfTemplatePath: string,
  employees: OrgChartEmployee[],
  leaderLogin: string,
  onProgress?: (current: number, total: number) => void
): Promise<Blob> {
  const validEmployees = employees.filter((employee) => hasValidJobLevel(employee));
  const skippedCount = employees.length - validEmployees.length;

  if (skippedCount > 0) {
    logger.info('Filtered out employees without valid jobLevel', {
      originalCount: employees.length,
      validCount: validEmployees.length,
      skippedCount
    });
  }

  logger.info('Starting bulk PDF generation with folder structure', { totalEmployees: validEmployees.length });

  const zip = new JSZip();
  let processedCount = 0;
  const failedEmployees: { login: string; error: string }[] = [];

  const employeeMap = new Map<string, OrgChartEmployee>();
  for (const employee of validEmployees) {
    employeeMap.set(employee.login, employee);
  }

  const employeeLogins = validEmployees.map((e) => e.login);

  const quarterStr = calculateCurrentQuarterForFilename();

  for (let i = 0; i < employeeLogins.length; i += BATCH_SIZE) {
    const batch = employeeLogins.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (login) => {
        try {
          const result = await generatePdfForEmployee(pdfTemplatePath, login);
          return {
            login,
            pdfBlob: result.pdfBlob,
            employeeFullName: result.employeeFullName,
            hadApiError: result.hadApiError,
            success: true,
            error: null
          };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          logger.warn('Failed to generate PDF for employee', { login, error: errorMessage });
          return { login, pdfBlob: null, employeeFullName: null, hadApiError: false, success: false, error: errorMessage };
        }
      })
    );

    for (const result of results) {
      if (result.success && result.pdfBlob && result.employeeFullName) {
        const employee = employeeMap.get(result.login);
        const sanitizedName = sanitizeFilename(result.employeeFullName);
        const filename = `${sanitizedName}_Talent_Profile_${quarterStr}.pdf`;

        // Place PDFs with API errors in _errors folder
        // Use getLocalDateForZip() to ensure file timestamps display as local time
        if (result.hadApiError) {
          const errorFilePath = `_errors/${filename}`;
          ensureFoldersWithLocalTimestamp(zip, errorFilePath);
          zip.file(errorFilePath, result.pdfBlob, { date: getLocalDateForJSZip() });
        } else {
          let folderPath = '';
          if (employee) {
            const nestedPath = buildNestedFolderPath(employee, employeeMap, leaderLogin);
            folderPath = nestedPath + filename;
          } else {
            folderPath = filename;
          }
          ensureFoldersWithLocalTimestamp(zip, folderPath);
          zip.file(folderPath, result.pdfBlob, { date: getLocalDateForJSZip() });
        }
      } else if (!result.success && result.error) {
        failedEmployees.push({ login: result.login, error: result.error });
      }
    }

    processedCount += batch.length;
    if (onProgress) {
      onProgress(processedCount, employeeLogins.length);
    }

    if (i + BATCH_SIZE < employeeLogins.length) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  // Add error folder with failed employee details (inside leader's folder)
  if (failedEmployees.length > 0) {
    const errorSummary = failedEmployees.map((f) => `${f.login}: ${f.error}`).join('\n\n');
    const errorFilePath = `${leaderLogin}/_errors/generation_errors.txt`;
    ensureFoldersWithLocalTimestamp(zip, errorFilePath);
    zip.file(errorFilePath, `Failed to generate PDFs for ${failedEmployees.length} employee(s):\n\n${errorSummary}`, {
      date: getLocalDateForJSZip()
    });

    logger.warn('Some PDFs failed to generate', { failedCount: failedEmployees.length, failedLogins: failedEmployees.map((f) => f.login) });
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });

  logger.info('Bulk PDF generation completed', {
    totalGenerated: Object.keys(zip.files).length - (failedEmployees.length > 0 ? 1 : 0),
    failedCount: failedEmployees.length
  });

  return zipBlob;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
